/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { observer } from 'mobx-react';
import cm from 'classnames';
import { FormContext } from 'ksnm-common-ui/lib/Form/FormWrapper/FormWrapperContext';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';
import type { FC } from 'react';

import { confirmDialogStore } from '@stores/confirmDialog';
import { designDrawerStore } from '@stores/designDrawerStore';

import styles from './FormWithActions.module.scss';

export interface PermissionStoreInterface {
  hiddenEdit?: boolean;
  hiddenDelete?: boolean;
  hiddenClean?: boolean;
  hiddenSave?: boolean;
  setHiddenEdit?: (flag: boolean) => void;
  setHiddenDelete?: (flag: boolean) => void;
  setHiddenClean?: (flag: boolean) => void;
  setHiddenSave?: (flag: boolean) => void;
  isNew?: boolean;
}

export interface StoreInterface extends PermissionStoreInterface {
  disableEdit?: boolean;
  disableDelete?: boolean;
  isNew: boolean;
  editMode: boolean;
  setEditMode: (editMode: boolean) => void;
  delete?: () => Promise<void>;
  isEditMode: boolean;
}

interface FormWithActionsInterface {
  children: JSX.Element | JSX.Element[] | string;
  header?: JSX.Element | JSX.Element[] | string;
  store: StoreInterface;
  goBackLink: () => void;
  withoutStyles?: boolean;
  actions?: JSX.Element | null;
  withCancelBtn?: boolean;
  exitTooltip?: string;
  afterSubmitAction?: () => void;
  beforeSubmitAction?: (values: Record<string, any>) => Promise<void>;
  checkInvalidFields?: boolean;
  tabs?: {
    items: { name: string; value: string }[];
    active: string;
    onChange: (value: string) => void;
  };
  allowDeleteWithoutEdit?: boolean;
  needSaveExit?: boolean;
  needFormStyle?: boolean;
}

export const FormWithActionsComponent: FC<FormWithActionsInterface> = ({
  children,
  goBackLink,
  store,
  withoutStyles,
  actions,
  header,
  withCancelBtn,
  exitTooltip: exitTooltipInit,
  afterSubmitAction,
  beforeSubmitAction,
  checkInvalidFields = true,
  tabs,
  allowDeleteWithoutEdit,
  needSaveExit = true,
  needFormStyle = true,
}: FormWithActionsInterface) => {
  const {
    isNew,
    editMode,
    setEditMode,
    delete: deleteEntity,
    isEditMode,
    hiddenEdit,
    hiddenDelete,
    disableEdit,
    disableDelete,
    hiddenClean,
    hiddenSave,
  } = store;
  const { hideInfo, setErrors } = designDrawerStore;

  const { submit, reset } = useForm();
  const { isDirty } = useContext(FormContext);
  const { errors, submitErrors, dirtyFieldsSinceLastSubmit, values } = useFormState({
    subscription: { errors: true, submitErrors: true, dirtyFieldsSinceLastSubmit: true, values: true },
  });

  useEffect(() => {
    hideInfo();
    setErrors({ ...errors, ...submitErrors });

    return () => setErrors({});
  }, [errors, setErrors, hideInfo, submitErrors]);

  const onReset = useCallback(() => reset(), [reset]);
  const onEdit = useCallback(() => setEditMode(true), [setEditMode]);

  const onSave = useCallback(async () => {
    await beforeSubmitAction?.(values);

    confirmDialogStore.open({
      text: 'Сохранить изменения?',
      onOk: () => {
        submit();
        afterSubmitAction?.();
      },
    });
  }, [beforeSubmitAction, values, submit, afterSubmitAction]);

  const onExit = useCallback(() => {
    if (!isDirty || !needSaveExit || !editMode) {
      goBackLink();
      return;
    }

    confirmDialogStore.open({
      text: 'Сохранить изменения, перед выходом?',
      onOk: async () => {
        await submit();
        afterSubmitAction?.();
        goBackLink();
      },
      onCancel: () => goBackLink(),
    });
  }, [isDirty, needSaveExit, goBackLink, submit, afterSubmitAction, editMode]);

  const onDelete = useCallback(
    () =>
      confirmDialogStore.open({
        text: 'Удалить элемент?',
        onOk: async () => {
          await deleteEntity?.();
          goBackLink();
        },
      }),
    [deleteEntity, goBackLink],
  );

  const allInvalidFieldsChanged = useMemo(() => {
    if (!checkInvalidFields) return true;

    return Object.keys(submitErrors ?? {}).every((field: string) =>
      Object.keys(dirtyFieldsSinceLastSubmit ?? {}).includes(field),
    );
  }, [checkInvalidFields, dirtyFieldsSinceLastSubmit, submitErrors]);

  const onCancel = useCallback(() => {
    if (isDirty) {
      confirmDialogStore.open({
        text: 'Сохранить изменения?',
        onOk: async () => {
          await submit();
          setEditMode(false);
          reset();
        },
        onCancel: () => {
          setEditMode(true);
          reset();
        },
      });
    } else {
      setEditMode(false);
    }
  }, [isDirty, reset, setEditMode, submit]);

  const exitTooltip = useMemo(() => exitTooltipInit ?? 'Вернуться в справочник', [exitTooltipInit]);

  const disableDeleteBtn = useMemo(() => {
    const disable = isNew || disableDelete;
    if (allowDeleteWithoutEdit) return disable;
    else return !editMode || disable;
  }, [allowDeleteWithoutEdit, disableDelete, editMode, isNew]);

  return (
    <>
      {!!header && <div>{header}</div>}
      <div className={styles.actionPanel}>
        {!hiddenDelete && (
          <IconButton iconName="delete" disabled={disableDeleteBtn} onClick={onDelete} title="Удалить" />
        )}

        {!hiddenEdit && (
          <IconButton
            iconName="edit"
            disabled={editMode || isNew || disableEdit}
            onClick={onEdit}
            title="Редактировать"
          />
        )}

        {!hiddenClean && <IconButton iconName="stop" onClick={onReset} disabled={!isDirty} title="Сбросить форму" />}

        {!hiddenSave && (
          <IconButton
            iconName="save"
            onClick={onSave}
            disabled={!isDirty || !allInvalidFieldsChanged}
            title="Сохранить"
          />
        )}

        {withCancelBtn && (
          <IconButton iconName="cancelArrow" onClick={onCancel} disabled={!editMode || isNew} title="Отменить" />
        )}

        {actions}

        <IconButton iconName="exit" onClick={onExit} title={exitTooltip} />
      </div>

      {tabs && (
        <ul className={styles.tabs}>
          {tabs.items.map((tab, index) => (
            <li
              key={index}
              className={cm(styles.tab, { [styles.active]: tab.value === tabs.active })}
              onClick={() => tabs.onChange(tab.value)}
            >
              {tab.name}
            </li>
          ))}
        </ul>
      )}

      <div
        className={
          withoutStyles ? '' : cm({ [styles.disabled]: !isEditMode, [styles.formWrapper]: !header && needFormStyle })
        }
      >
        {children}
      </div>
    </>
  );
};

export const FormWithActions = observer(FormWithActionsComponent);
